import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface StepperStep {
  /** Stable id used as React key. */
  id: string;
  /** Short label shown beneath the bubble. */
  label: string;
}

interface StepperProps {
  steps: StepperStep[];
  /** 1-based index of the currently active (or "next to do") step. */
  currentStep: number;
  /** Steps strictly less than this index render as completed (green check). */
  completedThrough?: number;
  className?: string;
}

/**
 * Two-step (or N-step) progress indicator used at the top of the
 * MappingLocationPage. Built on shadcn/Tailwind primitives — no extra
 * dependency. Intentionally compact to live in a sticky-ish header on mobile.
 */
export function Stepper({
  steps,
  currentStep,
  completedThrough = 0,
  className,
}: StepperProps) {
  return (
    <ol
      className={cn(
        "flex w-full items-center gap-2",
        className,
      )}
      aria-label="Mapping progress"
    >
      {steps.map((step, idx) => {
        const stepNumber = idx + 1;
        const isCompleted = stepNumber <= completedThrough;
        const isActive = !isCompleted && stepNumber === currentStep;
        const isLast = idx === steps.length - 1;
        return (
          <li
            key={step.id}
            className="flex flex-1 items-center gap-2"
            aria-current={isActive ? "step" : undefined}
          >
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums transition-colors",
                  isCompleted && "bg-green-100 text-green-800 ring-1 ring-green-300",
                  isActive && "bg-primary text-primary-foreground ring-2 ring-primary/30",
                  !isCompleted && !isActive && "bg-muted text-muted-foreground",
                )}
                aria-hidden="true"
              >
                {isCompleted ? <Check className="h-4 w-4" strokeWidth={3} /> : stepNumber}
              </span>
              <span
                className={cn(
                  "truncate text-sm font-medium",
                  isCompleted && "text-green-800",
                  isActive && "text-foreground",
                  !isCompleted && !isActive && "text-muted-foreground",
                )}
              >
                {step.label}
              </span>
            </div>
            {!isLast && (
              <span
                aria-hidden="true"
                className={cn(
                  "h-px flex-1 bg-border transition-colors",
                  isCompleted && "bg-green-300",
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
